import { useState, useEffect } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { PageName } from '../types';
import { getAbsoluteNavigationPaths } from '../../../utils/navigation';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getHelpDocConfig } from '../../helpDocs/helpDocsConfig';
import HelpDocDialog from '../../helpDocs/components/HelpDocDialog';
import { HelpDocIcon } from '../../common/icons.tsx';

const {
  reportIssuePath,
  dashboardPath,
  cellBoardMessagePath,
  issueConfigurationPath,
  crewingJobStationAssignmentsPath,
  crewingGuidePath,
  crewingEmployeeSearchPath,
  cellboardSummaryPath,
  supportHierarchyPath,
  schedulingToolCapacityUtilizationPath,
  schedulingToolSalesOrderPath,
  schedulingToolLaborWhatIfPath,
} = getAbsoluteNavigationPaths();

const routeToHelpLinkPage: Record<string, PageName> = {
  [dashboardPath]: 'Dashboard',
  [cellBoardMessagePath]: 'CellBoardMessage',
  [reportIssuePath]: 'IssueReport',
  [issueConfigurationPath]: 'IssueConfiguration',
  [crewingJobStationAssignmentsPath]: 'JobStationAssignments',
  [crewingGuidePath]: 'CrewingGuide',
  [crewingEmployeeSearchPath]: 'CrewingEmployeeSearch',
  [cellboardSummaryPath]: 'Scoreboard',
  [supportHierarchyPath]: 'SupportHierarchy',
  [schedulingToolCapacityUtilizationPath]: 'SchedulingToolCapacityUtilization',
  [schedulingToolSalesOrderPath]: 'SchedulingToolSalesOrders',
  [schedulingToolLaborWhatIfPath]: 'SchedulingToolLaborWhatIf',
};

/**
 * Renders a help icon that shows either:
 * 1. External help link in a new tab (priority)
 * 2. Internal documentation in a dialog (fallback)
 * 3. Nothing if neither exists for the current page
 */
const HelpLinkIcon = () => {
  const { t } = useTranslation();
  const pathname = useLocation().pathname;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [content, setContent] = useState<string>('');

  // --- External Help Links (priority) ---
  const helpLinks = useSelector((state: RootState) => state.app.helpLinks);
  const helpLinkPageName = routeToHelpLinkPage[pathname];
  const externalHelpLink = helpLinkPageName
    ? helpLinks.find((x) => x.pageName === helpLinkPageName)
    : undefined;

  // --- Internal Documentation (fallback) ---
  const internalDocConfig = getHelpDocConfig(pathname);

  /**
   * Fetches Markdown content when the dialog opens.
   */
  useEffect(() => {
    if (dialogOpen && internalDocConfig && !content) {
      fetch(internalDocConfig.url)
        .then((res) => res.text())
        .then(setContent);
    }
  }, [dialogOpen, internalDocConfig, content]);

  // 1. External help link takes priority
  if (externalHelpLink) {
    return (
      <Tooltip title={t('help')}>
        <IconButton
          size='large'
          component='a'
          href={externalHelpLink.linkUrl}
          target='_blank'
          rel='noreferrer'
          sx={{ color: 'white' }}
        >
          <HelpDocIcon />
        </IconButton>
      </Tooltip>
    );
  }

  // 2. Internal documentation as fallback
  if (internalDocConfig) {
    return (
      <>
        <Tooltip title={t('help')}>
          <IconButton
            size='large'
            onClick={() => setDialogOpen(true)}
            sx={{ color: 'white' }}
          >
            <HelpDocIcon />
          </IconButton>
        </Tooltip>
        <HelpDocDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={internalDocConfig.title}
          content={content}
        />
      </>
    );
  }

  // 3. No help available for this page
  return null;
};

export default HelpLinkIcon;
